import React, { useCallback, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Loader } from 'ncoded-component-library';
import LightbulbOnIcon from 'icons/LightbulbOn.icon';
import useQueryFilter from 'hooks/useQueryFilter';
import utils, {
  getArrayLength,
  getDataRoomRoute,
  getHasElementsInArray,
} from 'utils';
import DashCardHeading from 'router/subrouters/DataRoom/pages/Dashboard/componenets/DashCardHeading';
import SegmentedToggle from 'components/SegmentedToggle';
import useEnterpriseDealsQuery from './hooks/useEnterpriseDealsQuery';
import useEnterpriseSuggestedActionsQuery from './hooks/useEnterpriseSuggestedActionsQuery';
import useGeneralSuggestedActionsQuery from './hooks/useGeneralSuggestedActionsQuery';
import EnterpriseSuggestedActionsSection from './components/EnterpriseSuggestedActionsSection';
import EnterpriseSuggestedActionItem from './components/EnterpriseSuggestedActionItem';
import GeneralSuggestedActionItem from './components/GeneralSuggestedActionItem';
import EnterpriseSuggestedActionsPlaceholder from './components/EnterpriseSuggestedActionsPlaceholder';
import SuggestedActionTypeFilter from './components/SuggestedActionTypeFilter';
import SuggestedActionsDealFilter from './components/SuggestedActionsDealFilter';
import DefaultSuggestedActionsEmptyState from 'components/DefaultSuggestedActionsEmptyState';
import ResolvePagination from 'components/ResolvePagination';
import useResetPage from 'router/subrouters/Dash/subrouters/hooks/useResetPage';
import { FIRST_PAGE, SMOOTH_SCROLL_CONFIGURATION } from 'data/global';
import {
  HAS_ACTIONS_FILTER,
  SUGGESTED_ACTIONS_ACTION_TYPE_KEY,
  SUGGESTED_ACTIONS_HAS_ACTIONS_KEY,
} from './suggestedActionsFilterData';
import { Link } from 'react-router-dom';

import './EnterpriseSuggestedActions.styles.scss';

export const SUGGESTED_ACTIONS_DEAL_FILTER_KEY = 'suggestedActionsDealRoomId';
export const SUGGESTED_ACTIONS_PAGE_KEY = 'suggestedActionsPage';

type EnterpriseSuggestedActionsProps = {
  className?: string;
  enterpriseId: string;
};

const EnterpriseSuggestedActions: React.FC<EnterpriseSuggestedActionsProps> = (
  props,
) => {
  const { className, enterpriseId } = props;

  const { t } = useTranslation();

  const { data: deals, isLoading: isLoadingDeals } =
    useEnterpriseDealsQuery(enterpriseId);

  const { params, onFiltersApply, applyQueryFilter } = useQueryFilter();

  const page = params[SUGGESTED_ACTIONS_PAGE_KEY];

  const hasActionsFilter =
    params[SUGGESTED_ACTIONS_HAS_ACTIONS_KEY] || HAS_ACTIONS_FILTER.ALL;

  const dealRoomIds = useMemo(() => {
    const selected = utils.mapParamsToArray(
      params[SUGGESTED_ACTIONS_DEAL_FILTER_KEY],
    );
    return getHasElementsInArray(selected) ? selected : undefined;
  }, [params]);

  const hasSuggestedActions = useMemo(() => {
    if (hasActionsFilter === HAS_ACTIONS_FILTER.WITH_ACTIONS) return true;
    if (hasActionsFilter === HAS_ACTIONS_FILTER.WITHOUT_ACTIONS) return false;
    return undefined;
  }, [hasActionsFilter]);

  const actionTypes = useMemo(() => {
    const selected = utils.mapParamsToArray(
      params[SUGGESTED_ACTIONS_ACTION_TYPE_KEY],
    );
    return getHasElementsInArray(selected) ? selected : undefined;
  }, [params]);

  const suggestedActionsFilters = useMemo(
    () => ({ hasSuggestedActions, actionTypes }),
    [hasSuggestedActions, actionTypes],
  );

  const { data, isLoading: isLoadingSuggestions } =
    useEnterpriseSuggestedActionsQuery(
      enterpriseId,
      dealRoomIds,
      page,
      suggestedActionsFilters,
    );

  const handleHasActionsChange = useCallback(
    (value: string | number | boolean) => {
      onFiltersApply(
        SUGGESTED_ACTIONS_HAS_ACTIONS_KEY,
        value === HAS_ACTIONS_FILTER.ALL ? '' : String(value),
      );
      applyQueryFilter(SUGGESTED_ACTIONS_PAGE_KEY, FIRST_PAGE);
    },
    [onFiltersApply, applyQueryFilter],
  );

  const hasActionsToggleOptions = useMemo(
    () => [
      { label: t('allDeals'), value: HAS_ACTIONS_FILTER.ALL },
      { label: t('withActions'), value: HAS_ACTIONS_FILTER.WITH_ACTIONS },
      { label: t('withoutActions'), value: HAS_ACTIONS_FILTER.WITHOUT_ACTIONS },
    ],
    [t],
  );

  const { data: generalActions, isLoading: isLoadingGeneralActions } =
    useGeneralSuggestedActionsQuery();

  useResetPage(
    data?.totalPages || 0,
    page || FIRST_PAGE,
    SUGGESTED_ACTIONS_PAGE_KEY,
  );

  const dealOptions = useMemo(
    () =>
      (deals || []).map(({ _id, name }) => ({
        value: _id,
        label: name,
      })),
    [deals],
  );

  const isLoading =
    isLoadingDeals || isLoadingSuggestions || isLoadingGeneralActions;

  const isFirstPage = !page || page === FIRST_PAGE;

  // The personal suggestions are user-level (not paginated), so they are only
  // shown before the first page of Data Room suggestions.
  const visibleGeneralActions = useMemo(
    () => (isFirstPage ? generalActions || [] : []),
    [generalActions, isFirstPage],
  );

  const visibleDataRooms = useMemo(() => data?.items || [], [data?.items]);

  const numberOfActiveElements = useMemo(
    () =>
      (data?.items || []).reduce(
        (total, { actions }) => total + getArrayLength(actions),
        getArrayLength(visibleGeneralActions),
      ),
    [data?.items, visibleGeneralActions],
  );

  const hasPersonalSuggestions = getHasElementsInArray(visibleGeneralActions);

  const hasVisibleSuggestions =
    getHasElementsInArray(visibleDataRooms) || hasPersonalSuggestions;

  const containerRef = useRef<HTMLDivElement>(null);

  const scrollToStart = useCallback(
    () =>
      setTimeout(
        () =>
          containerRef.current?.scrollIntoView({
            ...SMOOTH_SCROLL_CONFIGURATION,
            block: 'start',
          }),
        0,
      ),
    [],
  );

  const classes = classNames(
    'enterprise-suggested-actions',
    'white-panel',
    className,
  );

  return (
    <div ref={containerRef} className={classes}>
      <DashCardHeading
        heading={t('suggestedActionsLc')}
        icon={LightbulbOnIcon}
        numberOfElements={numberOfActiveElements}
        showTimeframe={false}
      />

      <div className="enterprise-suggested-actions__filters">
        <SegmentedToggle
          className="enterprise-suggested-actions__filters__toggle"
          options={hasActionsToggleOptions}
          selectedValue={hasActionsFilter}
          onChange={handleHasActionsChange}
        />

        <div className="enterprise-suggested-actions__filters__actions">
          <SuggestedActionTypeFilter pageKey={SUGGESTED_ACTIONS_PAGE_KEY} />

          <SuggestedActionsDealFilter
            filterKey={SUGGESTED_ACTIONS_DEAL_FILTER_KEY}
            pageKey={SUGGESTED_ACTIONS_PAGE_KEY}
            options={dealOptions}
          />
        </div>
      </div>

      {isLoadingSuggestions && <EnterpriseSuggestedActionsPlaceholder />}

      {isLoading ? (
        <Loader overlay={false} />
      ) : !hasVisibleSuggestions ? (
        <DefaultSuggestedActionsEmptyState />
      ) : (
        <div className="enterprise-suggested-actions__wrapper">
          {hasPersonalSuggestions && (
            <EnterpriseSuggestedActionsSection
              title={t('personal')}
              count={getArrayLength(visibleGeneralActions)}
            >
              {visibleGeneralActions.map((suggestion) => (
                <GeneralSuggestedActionItem
                  key={suggestion.type}
                  suggestion={suggestion}
                />
              ))}
            </EnterpriseSuggestedActionsSection>
          )}
          {visibleDataRooms.map(
            ({ dataRoomId, dealRoomId, dealRoomName, actions }) => (
              <EnterpriseSuggestedActionsSection
                key={dataRoomId}
                title={dealRoomName}
                count={getArrayLength(actions)}
              >
                {getHasElementsInArray(actions) ? (
                  actions.map((suggestion) => (
                    <EnterpriseSuggestedActionItem
                      key={suggestion.type}
                      dataRoomId={dataRoomId}
                      dealRoomId={dealRoomId}
                      suggestion={suggestion}
                    />
                  ))
                ) : (
                  <DefaultSuggestedActionsEmptyState
                    className="no-actions-for-data-room"
                    title={t('youAreUpToDate')}
                  >
                    <p>{t('noSuggestedActionsForDataRoom')}</p>
                    <Link
                      className="ncoded-button ncoded-button--outline"
                      to={getDataRoomRoute(dataRoomId)}
                    >
                      {t('goToDataRoom')}
                    </Link>
                  </DefaultSuggestedActionsEmptyState>
                )}
              </EnterpriseSuggestedActionsSection>
            ),
          )}
        </div>
      )}

      <ResolvePagination
        totalPages={data?.totalPages || 0}
        pageParam={SUGGESTED_ACTIONS_PAGE_KEY}
        onClick={scrollToStart}
      />
    </div>
  );
};

export default EnterpriseSuggestedActions;
